import React, { useEffect, useRef, useState } from 'react';
import { SubscriptionResult, useSubscribe, useSubscriberStatus } from '../../../hooks';
import { GuideDownloadDialog } from '../../guide-download';
import { markSubscribed } from '../../../utils/subscriber-storage';
import styles from './subscriber-form.module.css';

interface FormData {
  email: string;
  firstName: string;
}

function validateFormData(data: FormData): { isValid: boolean; error?: string } {
  if (!data.email.trim()) {
    return { isValid: false, error: 'Email is required' };
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(data.email.trim())) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  if (data.firstName.trim().length > 50) {
    return { isValid: false, error: 'First name is too long' };
  }
  if (data.email.trim().length > 100) {
    return { isValid: false, error: 'Email is too long' };
  }
  return { isValid: true };
}

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default function SubscriberForm() {
  const [formData, setFormData] = useState<FormData>({ email: '', firstName: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SubscriptionResult | null>();
  const [error, setError] = useState<string | null>(null);
  const [showGuideDialog, setShowGuideDialog] = useState(false);
  const { hasSubscribed: isReturnSubscriber, subscriberInfo } = useSubscriberStatus();
  const subscribeUser = useSubscribe();

  const TURNSTILE_SITE_KEY = '0x4AAAAAACBP9GGW1Qi7mScz';
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [turnstileReady, setTurnstileReady] = useState<boolean>(false);
  const [widgetId, setWidgetId] = useState<any>(null);
  const [formTs, setFormTs] = useState<number>(() => Date.now());
  // Honeypot disabled to avoid blocking real subscribers (e.g. from auto-complete). Re-enable when ready.
  // const honeypotRef = useRef<HTMLInputElement | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  useEffect(() => {
    setFormTs(Date.now());

    function loadTurnstileScript(): Promise<void> {
      return new Promise((resolve) => {
        // @ts-ignore
        if (window.turnstile) {
          resolve();
          return;
        }
        if (isLocalhost) {
          resolve();
          return;
        }
        const existing = document.querySelector('script[data-turnstile-script="true"]') as HTMLScriptElement | null;
        if (existing) {
          existing.addEventListener('load', () => resolve());
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = true;
        script.defer = true;
        script.setAttribute('data-turnstile-script', 'true');
        script.onload = () => resolve();
        document.head.appendChild(script);
      });
    }

    loadTurnstileScript().then(() => {
      if (isLocalhost) {
        setTurnstileReady(true);
        return;
      }
      // @ts-ignore
      if (window.turnstile && turnstileContainerRef.current) {
        // @ts-ignore
        const id = window.turnstile.render(turnstileContainerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          size: 'invisible',
          action: 'newsletter_signup',
          callback: (token: string) => setCaptchaToken(token),
          'expired-callback': () => setCaptchaToken(''),
          'error-callback': () => setCaptchaToken(''),
        });
        setWidgetId(id);
        setTurnstileReady(true);
      }
    });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    try { window.analytics?.trackEvent('newsletter_subscription_attempt', { page_section: 'subscribe_section' }); } catch { }

    const validation = validateFormData(formData);
    if (!validation.isValid) {
      setLoading(false);
      setError(validation.error || 'Invalid input');
      try { window.analytics?.trackEvent('newsletter_form_validation_error', { error_type: 'client_validation', page_section: 'subscribe_section' }); } catch { }
      return;
    }

    if (!isLocalhost) {
      if (!turnstileReady || !widgetId) {
        setLoading(false);
        setError('Human check not ready yet, please try again.');
        return;
      }
      try {
        // @ts-ignore
        window.turnstile.execute(widgetId);
        const waitForToken = async () => {
          const maxWaitMs = 3000;
          const start = Date.now();
          while (!captchaToken && Date.now() - start < maxWaitMs) {
            await new Promise(r => setTimeout(r, 50));
          }
        };
        await waitForToken();
      } catch {
        setLoading(false);
        return;
      }
    }
    const res = await subscribeUser({
      ...formData,
      lastName: '',
      turnstileToken: isLocalhost ? '' : (captchaToken || ''),
      honeypot: '', // Honeypot disabled - empty string passes server check
      formTs,
    });
    setLoading(false);
    setResult(res);

    if (!res.success) {
      setError(res.message);
      try { window.analytics?.trackEvent('newsletter_subscription_failure', { page_section: 'subscribe_section' }); } catch { }
    } else {
      markSubscribed({ email: formData.email, firstName: formData.firstName });
      try { window.analytics?.trackEvent('newsletter_subscription_success', { page_section: 'subscribe_section' }); } catch { }
      // try { window.analytics?.trackEvent('sign_up', { method: 'newsletter', page_section: 'subscribe_section' }); } catch { }
      setShowGuideDialog(true);
    }
  };

  const showSuccessView = result?.success || isReturnSubscriber;

  if (showSuccessView) {
    return (
      <>
        <div className={styles.success}>
          <div className={styles.matchMessage}>
            <h3>
              {isReturnSubscriber
                ? `Welcome back${subscriberInfo?.firstName ? `, ${subscriberInfo.firstName}` : ''}!`
                : 'Welcome!'}
            </h3>
            <p>
              {isReturnSubscriber
                ? "You're already subscribed. Download your free health guides anytime, and you'll stay up to date on events and our upcoming podcast."
                : "Thanks for subscribing! Download your free health guides below, and you'll be the first to hear about updates, events, and our upcoming podcast."}
            </p>
            <button
              type="button"
              className={styles.downloadButton}
              onClick={() => setShowGuideDialog(true)}
            >
              Download the Guides
            </button>
          </div>
        </div>
        <GuideDownloadDialog open={showGuideDialog} onClose={() => setShowGuideDialog(false)} />
      </>
    );
  }

  return (
    <div className={styles.layout}>
      <div className={styles.askColumn}>
        <h2 className={styles.headline}>Access the Free Health Guides</h2>
        <p className={styles.subhead}>
          Enter your email to instantly download the essential guides to HRT and breast health, and be the first to know when our weekly podcast launches.
        </p>

        <form className={styles.form} onSubmit={handleSubmit}>
          {/* Honeypot disabled - send empty string so server passes. Re-enable when ready.
          <input
            ref={honeypotRef}
            type="text"
            name="phone_extension"
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            style={{ position: 'absolute', left: '-5000px', height: 0, width: 0, opacity: 0 }}
            defaultValue=""
          />
          */}
          <div ref={turnstileContainerRef} style={{ display: 'none' }} />

          <input
            className={styles.input}
            type="text"
            name="firstName"
            placeholder="First Name"
            autoComplete="given-name"
            aria-label="First name"
            value={formData.firstName}
            onChange={handleChange}
          />
          <input
            className={styles.input}
            type="email"
            name="email"
            placeholder="Email Address*"
            autoComplete="email"
            aria-label="Email address"
            required
            value={formData.email}
            onChange={handleChange}
          />

          <button className={styles.button} type="submit" disabled={loading}>
            {loading ? 'Signing Up...' : 'Download the Guides'}
          </button>

          {error && <div className={styles.error}>{error}</div>}
        </form>

        <p className={styles.disclaimer}>
          We will never sell your information. Unsubscribe at any time.
        </p>
      </div>

      <div className={styles.valueColumn}>
        <div className={styles.includesCard}>
          <h3 className={styles.includesHeader}>Sign up to receive:</h3>
          <ul className={styles.includesList}>
            <li><CheckIcon /> Top questions about HRT answered</li>
            <li><CheckIcon /> 6 surprising facts about breast health</li>
            <li><CheckIcon /> More guides and resources</li>
            <li><CheckIcon /> Early access to our new podcast</li>            
          </ul>
        </div>
      </div>
    </div>
  );
}
